/**
 * BlogPage - Blog listing page with category filters and search
 */

import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { BookOpen, Rss } from "lucide-react";
import { SEO } from "../components/SEO";
import { BlogCard, BlogHeader, CategoryFilter } from "../components/blog";
import {
  ALL_POSTS,
  getPostsByCategory,
  searchPosts,
  getBlogMeta,
} from "../content/blog";
import type { BlogCategory } from "../content/blog/types";

export function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState<BlogCategory | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");

  const blogMeta = useMemo(() => getBlogMeta(), []);

  // Filter posts based on category and search
  const filteredPosts = useMemo(() => {
    let posts = selectedCategory
      ? getPostsByCategory(selectedCategory)
      : ALL_POSTS;

    if (searchQuery.trim()) {
      const searchResults = searchPosts(searchQuery);
      posts = posts.filter((post) =>
        searchResults.some((r) => r.slug === post.slug),
      );
    }

    return posts;
  }, [selectedCategory, searchQuery]);

  // Get featured post (first post)
  const featuredPost = filteredPosts[0];
  const remainingPosts = filteredPosts.slice(1);

  return (
    <>
      <SEO />
      <div className="space-y-8">
        {/* Header */}
        <BlogHeader searchQuery={searchQuery} onSearchChange={setSearchQuery} />

        {/* Category Filter */}
        <div>
          <h2 className="text-sm font-medium text-gray-700 mb-3">
            Browse by Topic
          </h2>
          <CategoryFilter
            categories={blogMeta.categories}
            selectedCategory={selectedCategory}
            onSelect={setSelectedCategory}
          />
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between">
          <p className="text-gray-600">
            {filteredPosts.length}{" "}
            {filteredPosts.length === 1 ? "article" : "articles"}
            {selectedCategory && ` in ${selectedCategory.replace("-", " ")}`}
            {searchQuery && ` matching "${searchQuery}"`}
          </p>
        </div>

        {/* Posts Grid */}
        {filteredPosts.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No articles found</p>
            <p className="text-gray-400 text-sm mt-1">
              Try adjusting your search or filter criteria
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Featured Post (if we have posts and no specific search/category) */}
            {featuredPost && !searchQuery && !selectedCategory && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <BlogCard post={featuredPost} variant="featured" />
              </motion.div>
            )}

            {/* Rest of posts in grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(searchQuery || selectedCategory
                ? filteredPosts
                : remainingPosts
              ).map((post, index) => (
                <motion.div
                  key={post.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <BlogCard post={post} />
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* RSS Feed Link */}
        <div className="text-center pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
            <Rss className="h-4 w-4" />
            More articles coming soon! Stay tuned for expert insights on Calgary
            building codes.
          </p>
        </div>
      </div>
    </>
  );
}

export default BlogPage;
